import type { Action, Issue, IssueKind, Severity } from '@/model/sourceState/sourcestate.generated';
import type {
  SourceIssueActionView,
  SourceIssueView,
  StatusTone,
} from '@/viewmodel/features/sourceState/types';

const SEVERITY_TONE: Record<Severity, StatusTone> = {
  info: 'info',
  warning: 'warning',
  error: 'danger',
};

const KIND_TITLE: Record<IssueKind, string> = {
  engine_rejected: 'sourceState.issue.kind.engineRejected',
  deletion_failed: 'sourceState.issue.kind.deletionFailed',
  connection_failed: 'sourceState.issue.kind.connectionFailed',
  source_failed: 'sourceState.issue.kind.sourceFailed',
  tables_failed: 'sourceState.issue.kind.tablesFailed',
  tables_missing: 'sourceState.issue.kind.tablesMissing',
  tables_empty: 'sourceState.issue.kind.tablesEmpty',
  stale: 'sourceState.issue.kind.stale',
};

const ACTION_VIEW: Record<Action, SourceIssueActionView> = {
  none: 'none',
  edit_connection: 'editConnection',
  edit_configuration: 'editConfiguration',
  retry_sync: 'retrySync',
  review_tables: 'reviewTables',
  delete_again: 'deleteAgain',
  contact_support: 'contactSupport',
  contact_owner: 'contactOwner',
};

const ACTION_LABEL: Partial<Record<SourceIssueActionView, string>> = {
  editConnection: 'sourceState.issue.action.editConnection',
  editConfiguration: 'sourceState.issue.action.editConfiguration',
  retrySync: 'sourceState.issue.action.retrySync',
  reviewTables: 'sourceState.issue.action.reviewTables',
  deleteAgain: 'sourceState.issue.action.deleteAgain',
};

const CATEGORY_CAUSE: Record<string, string> = {
  permission_denied: 'sourceState.issue.cause.permissionDenied',
  credential_invalid: 'sourceState.issue.cause.credentialInvalid',
  connectivity: 'sourceState.issue.cause.connectivity',
  configuration: 'sourceState.issue.cause.configuration',
  schema_break: 'sourceState.issue.cause.schemaBreak',
  internal: 'sourceState.issue.cause.internal',
};

const LAYER_KEY: Record<string, string> = {
  bronze: 'sourceState.issue.layer.bronze',
  silver: 'sourceState.issue.layer.silver',
  gold: 'sourceState.issue.layer.gold',
};

function nonEmpty(value: string | undefined | null): string | undefined {
  const trimmed = value?.trim();

  return trimmed ? trimmed : undefined;
}

export function toSourceIssueView(issue: Issue | null | undefined): SourceIssueView | undefined {
  if (!issue) return undefined;
  const category = nonEmpty(issue.category);
  const layer = nonEmpty(issue.layer);
  const action = ACTION_VIEW[issue.action] ?? 'none';

  return {
    kind: issue.kind,
    tone: SEVERITY_TONE[issue.severity] ?? 'neutral',
    isNotice: issue.severity === 'info',
    titleKey: KIND_TITLE[issue.kind] ?? 'sourceState.issue.kind.unknown',
    causeKey: category ? CATEGORY_CAUSE[category] : undefined,
    message: nonEmpty(issue.message),
    remediation: nonEmpty(issue.remediation?.summary),
    suggestedActions: issue.remediation?.suggested_actions ?? [],
    table: nonEmpty(issue.table),
    layer,
    layerKey: layer ? LAYER_KEY[layer] : undefined,
    stage: nonEmpty(issue.stage),
    affectedTables: issue.affected_tables ?? 0,
    occurredAt: issue.occurred_at ?? undefined,
    action,
    guidanceKey: `sourceState.issue.guidance.${action}`,
    actionKey: ACTION_LABEL[action],
    isUserActionable: issue.is_user_actionable,
    category,
    reason: nonEmpty(issue.reason),
  };
}
